import React, { useState, useEffect, useCallback } from 'react';

const EventManager = () => {
  const [events, setEvents] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [autoSeatOnRegister, setAutoSeatOnRegister] = useState(false);

  // Form states
  const [eventName, setEventName] = useState('');
  const [gameType, setGameType] = useState('NLH');
  const [eventDate, setEventDate] = useState('');
  const [buyInAmount, setBuyInAmount] = useState('');
  const [rakeAmount, setRakeAmount] = useState('');
  const [startingChips, setStartingChips] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('');
  const [selectedStructure, setSelectedStructure] = useState('');
  const [registrationOpens, setRegistrationOpens] = useState('');
  const [registrationCloses, setRegistrationCloses] = useState('');
  const [tournamentStartTime, setTournamentStartTime] = useState('');
  const [rebuyAllowed, setRebuyAllowed] = useState(false);
  const [rebuyAmount, setRebuyAmount] = useState('');
  const [rebuyDeadlineLevel, setRebuyDeadlineLevel] = useState('');
  const [rebuyChips, setRebuyChips] = useState('');
  const [addonAllowed, setAddonAllowed] = useState(false);
  const [addonAmount, setAddonAmount] = useState('');
  const [addonLevel, setAddonLevel] = useState('');
  const [addonChips, setAddonChips] = useState('');
  const [maxRebuys, setMaxRebuys] = useState('');
  const [maxAddons, setMaxAddons] = useState('1');
  const [lpsEnabled, setLpsEnabled] = useState(false);
  const [lpsFee, setLpsFee] = useState('');

  const convertUTCToLocal = (utcTimestamp) => {
    if (!utcTimestamp) return '';
    
    // Create Date object from UTC timestamp
    const date = new Date(utcTimestamp);
    
    // Get local date/time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    // Return in datetime-local format (YYYY-MM-DDTHH:MM)
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  const loadData = useCallback(async () => {
    try {
      // Load events
      const eventsResponse = await fetch(`${getApiUrl()}/events`);
      const eventsData = await eventsResponse.json();
      setEvents(eventsData);

      // Load blind structures
      const structuresResponse = await fetch(`${getApiUrl()}/blind-structures`);
      const structuresData = await structuresResponse.json();
      setStructures(structuresData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

const resetForm = () => {
  setEventName('');
  setGameType('NLH');
  setEventDate('');
  setBuyInAmount('');
  setRakeAmount('');
  setStartingChips('');
  setMaxPlayers('');
  setSelectedStructure('');
  setRegistrationOpens('');
  setRegistrationCloses('');
  setTournamentStartTime('');
  setRebuyAllowed(false);
  setRebuyAmount('');
  setRebuyDeadlineLevel('');
  setRebuyChips('');
  setMaxRebuys('');  // Add this
  setAddonAllowed(false);
  setAddonAmount('');
  setAddonLevel('');
  setAddonChips('');
  setMaxAddons('1');  // Add this
  setEditingEvent(null);
};

  const startCreateEvent = () => {
    resetForm();
    setShowCreateForm(true);
  };

const startEditEvent = (event) => {
  setEditingEvent(event);
  setEventName(event.name);
  setGameType(event.game_type);
  setEventDate(event.event_date.split('T')[0]);
  setBuyInAmount(event.buy_in_amount);
  setRakeAmount(event.rake_amount);
  setStartingChips(event.starting_chips);
  setMaxPlayers(event.max_players || '');
  setSelectedStructure('');
  setRegistrationOpens(convertUTCToLocal(event.registration_opens));
  setTournamentStartTime(convertUTCToLocal(event.tournament_start_time));
  setRegistrationCloses(convertUTCToLocal(event.registration_closes));
  setAutoSeatOnRegister(event.auto_seat_on_register || false);
  
  // Rebuy/addon fields
  setRebuyAllowed(event.rebuy_allowed || false);
  setRebuyAmount(event.rebuy_amount || '');
  setRebuyDeadlineLevel(event.rebuy_deadline_level || '');
  setRebuyChips(event.rebuy_chips || '');
  setMaxRebuys(event.max_rebuys || '');  // Add this
  setAddonAllowed(event.addon_allowed || false);
  setAddonAmount(event.addon_amount || '');
  setAddonLevel(event.addon_level || '');
  setAddonChips(event.addon_chips || '');
  setMaxAddons(event.max_addons || '1');  // Add this
  // ADD LPS settings:
  setLpsEnabled(event.lps_enabled || false);
  setLpsFee(event.lps_fee || '');
  
  setShowCreateForm(true);
};

const saveEvent = async () => {
  if (!eventName || !gameType || !eventDate || !buyInAmount || !startingChips) {
    alert('Please fill in all required fields');
    return;
  }

  try {
    const prizePoolAmount = parseFloat(buyInAmount) - parseFloat(rakeAmount || 0);
    
    const eventData = {
      name: eventName,
      game_type: gameType,
      event_date: eventDate,
      buy_in_amount: parseFloat(buyInAmount),
      rake_amount: parseFloat(rakeAmount || 0),
      prize_pool_amount: prizePoolAmount,
      starting_chips: parseInt(startingChips),
      max_players: maxPlayers ? parseInt(maxPlayers) : null,
      registration_opens: registrationOpens || null,
      tournament_start_time: tournamentStartTime || null,
      registration_closes: registrationCloses || null,
      auto_seat_on_register: autoSeatOnRegister,
      
      // Rebuy/addon settings with limits
      rebuy_allowed: rebuyAllowed,
      rebuy_amount: rebuyAllowed ? parseFloat(rebuyAmount) || 0 : null,
      rebuy_deadline_level: rebuyAllowed ? parseInt(rebuyDeadlineLevel) || null : null,
      rebuy_chips: rebuyAllowed ? parseInt(rebuyChips) || null : null,
      max_rebuys: rebuyAllowed && maxRebuys ? parseInt(maxRebuys) : null,  // Add this
      addon_allowed: addonAllowed,
      addon_amount: addonAllowed ? parseFloat(addonAmount) || 0 : null,
      addon_level: addonAllowed ? parseInt(addonLevel) || null : null,
      addon_chips: addonAllowed ? parseInt(addonChips) || null : null,
      max_addons: addonAllowed && maxAddons ? parseInt(maxAddons) : 1,
      lps_enabled: lpsEnabled, 
      lps_fee: lpsEnabled ? parseFloat(lpsFee) : null
    };

      let response;
      if (editingEvent) {
        // Update existing event
        console.log('Event data being sent:', eventData);
        response = await fetch(`${getApiUrl()}/events/${editingEvent.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      } else {
        // Create new event
        console.log('Event data being sent:', eventData);
        response = await fetch(`${getApiUrl()}/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      }

      if (response.ok) {
        alert(editingEvent ? 'Event updated successfully!' : 'Event created successfully!');
        setShowCreateForm(false);
        resetForm();
        loadData();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/events/${eventId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadData();
        alert('Event deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const createTournamentFromEvent = async (eventId) => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ event_id: eventId })
      });

      if (response.ok) {
        const tournament = await response.json();
        alert(`Tournament created! Dealer Code: ${tournament.dealer_code}`);
        // Open tournament control in new tab
        window.open(`/control/${tournament.id}`, '_blank');
      }
    } catch (error) {
      console.error('Error creating tournament:', error);
    }
  };

  if (loading) {
    return <div className="container">Loading events...</div>;
  }

  return (
    <div className="container">
      <h1>Event Management</h1>
      
      {/* Create/Edit Event */}
      <div className="card">
        <h3>Event Management</h3>
        <button 
          className="btn btn-success" 
          onClick={startCreateEvent}
          style={{ marginRight: '10px' }}
        >
          Create New Event
        </button>
        
        {showCreateForm && (
          <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '5px' }}>
            <h4>{editingEvent ? 'Edit Event' : 'Create New Event'}</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div className="form-group">
                <label>Event Name: *</label>
                <input 
                  type="text"
                  className="form-control"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="e.g., Weekly No Limit Hold'em"
                />
              </div>
              
              <div className="form-group">
                <label>Game Type: *</label>
                <select 
                  className="form-control"
                  value={gameType}
                  onChange={(e) => setGameType(e.target.value)}
                >
                  <option value="NLH">No Limit Hold'em</option>
                  <option value="PLO">Pot Limit Omaha</option>
                  <option value="NLO">No Limit Omaha</option>
                  <option value="Teams">Teams Event</option>
                  <option value="Bounty">Bounty Event</option>
                </select>
              </div>

              <div className="form-group">
                <label>Event Date: *</label>
                <input 
                  type="date"
                  className="form-control"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Buy-in Amount: *</label>
                <input 
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={buyInAmount}
                  onChange={(e) => setBuyInAmount(e.target.value)}
                  placeholder="25.00"
                />
              </div>

              <div className="form-group">
                <label>Rake/Admin Fee:</label>
                <input 
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={rakeAmount}
                  onChange={(e) => setRakeAmount(e.target.value)}
                  placeholder="5.00"
                />
              </div>

              <div className="form-group">
                <label>Starting Chips: *</label>
                <input 
                  type="number"
                  className="form-control"
                  value={startingChips}
                  onChange={(e) => setStartingChips(e.target.value)}
                  placeholder="10000"
                />
              </div>

              <div className="form-group">
                <label>Max Players:</label>
                <input 
                  type="number"
                  className="form-control"
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(e.target.value)}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div className="form-group">
                <label>Registration Opens:</label>
                <input 
                  type="datetime-local"
                  className="form-control"
                  value={registrationOpens}
                  onChange={(e) => setRegistrationOpens(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Registration Closes:</label>
                <input 
                  type="datetime-local"
                  className="form-control"
                  value={registrationCloses}
                  onChange={(e) => setRegistrationCloses(e.target.value)}
                />
              </div>
{/* Rebuy/Addon Settings */}
<div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
  <h4 style={{ borderTop: '2px solid #ddd', paddingTop: '20px' }}>Rebuy & Addon Settings</h4>
</div>

{/* Rebuy Settings */}
<div className="form-group">
  <label>
    <input 
      type="checkbox"
      checked={rebuyAllowed}
      onChange={(e) => setRebuyAllowed(e.target.checked)}
    />
    {' '}Allow Rebuys
  </label>
</div>

{rebuyAllowed && (
  <>
    <div className="form-group">
      <label>Rebuy Amount ($):</label>
      <input 
        type="number"
        step="0.01"
        className="form-control"
        value={rebuyAmount}
        onChange={(e) => setRebuyAmount(e.target.value)}
        placeholder="30.00"
      />
    </div>

    <div className="form-group">
      <label>Rebuy Chips:</label>
      <input 
        type="number"
        className="form-control"
        value={rebuyChips}
        onChange={(e) => setRebuyChips(e.target.value)}
        placeholder="Same as starting chips"
      />
    </div>

    <div className="form-group">
      <label>Rebuy Deadline (Level):</label>
      <input 
        type="number"
        className="form-control"
        value={rebuyDeadlineLevel}
        onChange={(e) => setRebuyDeadlineLevel(e.target.value)}
        placeholder="e.g., 6 (rebuys close at end of level 6)"
      />
      <small style={{ color: '#666', fontSize: '12px' }}>
        Players can rebuy until the end of this level
      </small>
    </div>

    <div className="form-group">
      <label>Max Rebuys Per Player:</label>
      <input 
        type="number"
        className="form-control"
        value={maxRebuys}
        onChange={(e) => setMaxRebuys(e.target.value)}
        placeholder="Leave empty for unlimited"
      />
      <small style={{ color: '#666', fontSize: '12px' }}>
        Empty = unlimited rebuys
      </small>
    </div>
  </>
)}

{/* Addon Settings */}
<div className="form-group">
  <label>
    <input 
      type="checkbox"
      checked={addonAllowed}
      onChange={(e) => setAddonAllowed(e.target.checked)}
    />
    {' '}Allow Addons
  </label>
</div>

{addonAllowed && (
  <>
    <div className="form-group">
      <label>Addon Amount ($):</label>
      <input 
        type="number"
        step="1"
        className="form-control"
        value={addonAmount}
        onChange={(e) => setAddonAmount(e.target.value)}
        placeholder="10.00"
      />
    </div>

    <div className="form-group">
      <label>Addon Chips:</label>
      <input 
        type="number"
        className="form-control"
        value={addonChips}
        onChange={(e) => setAddonChips(e.target.value)}
        placeholder="10000"
      />
    </div>

    <div className="form-group">
      <label>Addon Available at Level:</label>
      <input 
        type="number"
        className="form-control"
        value={addonLevel}
        onChange={(e) => setAddonLevel(e.target.value)}
        placeholder="e.g., 6 (addon break)"
      />
      <small style={{ color: '#666', fontSize: '12px' }}>
        Players can take addon during this level
      </small>
    </div>

    <div className="form-group">
      <label>Max Addons Per Player:</label>
      <input 
        type="number"
        className="form-control"
        value={maxAddons}
        onChange={(e) => setMaxAddons(e.target.value)}
        placeholder="1"
      />
      <small style={{ color: '#666', fontSize: '12px' }}>
        Typically 1 addon per player
      </small>
    </div>
  </>
)}

{/* LPS Settings - ADD THIS ENTIRE SECTION */}
<div style={{ marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '4px' }}>
  <h4 style={{ marginTop: 0 }}>Last Person Standing (LPS) Settings</h4>
  <div className="form-group">
    <label>
      <input
        type="checkbox"
        checked={lpsEnabled}
        onChange={(e) => setLpsEnabled(e.target.checked)}
      />
      {' '}Enable Last Person Standing
    </label>
  </div>

  {lpsEnabled && (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '10px' }}>
      <div className="form-group">
        <label>LPS Fee:</label>
        <input
          type="number"
          step="0.01"
          className="form-control"
          value={lpsFee}
          onChange={(e) => setLpsFee(e.target.value)}
          placeholder="e.g., 10.00"
        />
      </div>
    </div>
  )}
</div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={autoSeatOnRegister}
              onChange={(e) => setAutoSeatOnRegister(e.target.checked)}
            />
            {' '}Auto-seat players as they register
          </label>
        </div>

		<div className="form-group">
		  <label>Tournament Start Time:</label>
		  <input 
		    type="datetime-local"
		    className="form-control"
		    value={tournamentStartTime}
		    onChange={(e) => setTournamentStartTime(e.target.value)}
		  />
		  <small style={{ color: '#666', fontSize: '12px' }}>
		    When the tournament clock starts (usually before registration closes)
		  </small>
		</div>
            </div>

            <div style={{ margin: '20px 0' }}>
              <strong>Prize Pool Contribution:</strong> ${(parseFloat(buyInAmount || 0) - parseFloat(rakeAmount || 0)).toFixed(2)} per player
            </div>

            <div>
              <button className="btn btn-success" onClick={saveEvent}>
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
              <button 
                className="btn" 
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                style={{ marginLeft: '10px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Existing Events */}
      <div className="card">
        <h3>Existing Events ({events.length})</h3>
        {events.length === 0 ? (
          <p>No events found. Create your first event above.</p>
        ) : (
          <div>
            {events.map(event => (
              <div key={event.id} className="card" style={{ margin: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4>{event.name}</h4>
                    <div style={{ color: '#666' }}>
                      <div>
                        {event.game_type} | ${event.buy_in_amount} buy-in (${event.rake_amount} rake) | 
                        {event.starting_chips.toLocaleString()} starting chips
                      </div>
                      <div>
                        Date: {new Date(event.event_date).toLocaleDateString()} | 
                        Prize Pool: ${event.prize_pool_amount} per player
                        {event.max_players && ` | Max: ${event.max_players} players`}
                      </div>
            		     <div>
                        Tournament Starts: {event.tournament_start_time ? new Date(event.tournament_start_time).toLocaleString() : 'Not set'}
                    </div>
                    <div>
                      Registration: {event.registration_opens ? new Date(event.registration_opens).toLocaleString() : 'TBD'} to {event.registration_closes ? new Date(event.registration_closes).toLocaleString() : 'TBD'}
                    </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <button 
                      className="btn btn-success"
                      onClick={() => createTournamentFromEvent(event.id)}
                    >
                      Create Tournament
                    </button>
                    <button 
                      className="btn"
                      onClick={() => startEditEvent(event)}
                    >
                      Edit Event
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => deleteEvent(event.id)}
                      style={{ fontSize: '12px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManager;
